// One measure of money, listing every currency it was earned in.
//
// THE RULE THIS COMPONENT EXISTS TO HOLD: money is never summed across
// currencies. A card takes a LIST — one row per currency — and has no total
// row and no way to render one. The previous shape (a KPI tile per currency)
// held the same rule but scattered it: three measures across two currencies
// became six tiles in one grid, and "Revenue · EUR" sat beside "DriveCars ·
// MAD" with nothing saying they answer different questions. Grouping by
// MEASURE and listing currencies inside makes the comparison the operator
// actually wants — gross against net against commission, same currency, same
// row position — a matter of reading across three cards.
//
// The empty state is a sentence, not a zero: "EUR 0.00" would be a claim that
// there were bookings worth nothing, which is not the same as there having
// been none.

export interface MoneyCardRow {
  currency: string;
  amount: number;
}

export interface MoneyCardProps {
  title: string;
  /** One entry per currency. Never a pre-merged total — see the note above. */
  rows: MoneyCardRow[];
  /** Shown under the title; what this measure means in one phrase. */
  hint?: string;
  /** While the figures are in flight the card shows its title and nothing
   *  else — an empty state during a load would say "no bookings" about a
   *  period nobody has answered for yet. */
  isLoading?: boolean;
  /** Overrides the default "No bookings in this period." */
  emptyText?: string;
}

/** The figure alone, grouped and always to two decimals — an operator
 *  reconciling against Stripe needs the cents.
 *
 *  Deliberately NOT `style: 'currency'`: the row prints the CODE in its own
 *  column so the three cards' numbers line up, and a currency-styled value
 *  would repeat it ("EUR  €12,345.00") and vary in width by symbol. It also
 *  sidesteps Intl throwing on a supplier's local code it does not know. */
function fmtAmount(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function MoneyCard({ title, rows, hint, isLoading, emptyText }: MoneyCardProps) {
  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
        {title}
      </div>
      {hint ? (
        <div className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">{hint}</div>
      ) : null}

      {isLoading ? (
        <div className="mt-3 text-[13px] text-[color:var(--bo-mute)]">…</div>
      ) : rows.length === 0 ? (
        <div className="mt-3 text-[12px] text-[color:var(--bo-mute)]">
          {emptyText ?? 'No bookings in this period'}
        </div>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {rows.map((r) => (
            <li key={r.currency} className="flex items-baseline gap-3">
              {/* The code leads and the figure is end-aligned, so three cards
                  side by side line their numbers up whatever the currency. */}
              <span className="text-[12px] font-medium text-[color:var(--bo-mute)]">
                {r.currency}
              </span>
              <span className="flex-1 text-end text-[18px] font-semibold tabular-nums">
                {fmtAmount(r.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
